import { gbp } from "@/lib/api";
import { Check, ChevronDown } from "lucide-react";
import { Fragment, useState } from "react";

const BK = process.env.REACT_APP_BACKEND_URL;

const FREQ_LABELS = {
  monthly: "Monthly recurring services", quarterly: "Quarterly services",
  annual: "Annual services (billed as shown)", one_off: "One-off fees",
  optional: "Optional extras — add any time (not in the totals below)",
};

const REC_TOTAL_LABEL = { monthly: "Total Monthly Fee", quarterly: "Total Quarterly Fee", annual: "Total Annual Fee" };

function recurringTotals(grouped, selectedOptionals) {
  const sel = selectedOptionals || [];
  return ["monthly", "quarterly", "annual"].map((f) => {
    const base = grouped[f] || [];
    const opt = (grouped.optional || []).filter((o) => o.billing_frequency === f && sel.includes(o.service_id));
    const all = [...base, ...opt];
    const net = all.reduce((a, s) => a + (Number(s.net) || 0), 0);
    const vat = all.reduce((a, s) => a + (Number(s.vat) || 0), 0);
    const hasIncluded = all.some((s) => s.presentation === "included");
    return { freq: f, count: all.length, net, vat, hasIncluded };
  }).filter((x) => x.count > 0 && (x.net > 0 || x.hasIncluded));
}

// A recurring frequency is worth showing only if it has a real fee or an explicitly "Included" line.
const isMeaningfulRecurring = (items) => (items || []).some((s) => (Number(s.net) || 0) > 0 || s.presentation === "included");

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function TotalBlock({ testid, totalTestid, net, vat, totalLabel, discount }) {
  const damt = discount ? Math.min(r2(discount.amount), net) : 0;
  const dVat = net ? r2((vat * damt) / net) : 0;
  const finalNet = r2(net - damt);
  const finalVat = r2(vat - dVat);
  return (
    <div className="bg-white/5 rounded-xl overflow-hidden mt-4" data-testid={testid}>
      <div className="flex justify-between px-5 py-2.5 border-b border-white/10"><span className="text-slate-300">Sub-total</span><span className="tabular text-slate-100">{gbp(net)}</span></div>
      {discount && damt > 0 && (
        <div className="flex justify-between px-5 py-2.5 border-b border-white/10" data-testid="discount-line"><span className="text-cass-teal">{discount.label}{discount.reason ? ` (${discount.reason})` : ""}</span><span className="tabular text-cass-teal">&minus; {gbp(damt)}</span></div>
      )}
      <div className="flex justify-between px-5 py-2.5 border-b border-white/10"><span className="text-slate-300">VAT</span><span className="tabular text-slate-100">{gbp(finalVat)}</span></div>
      <div className="flex justify-between px-5 py-3 bg-white/10"><span className="text-white font-semibold">{totalLabel}</span><span className="tabular font-bold text-white" data-testid={totalTestid}>{gbp(finalNet + finalVat)}</span></div>
    </div>
  );
}

function PriceCell({ s }) {
  if (s.presentation === "included")
    return <span className="text-cass-teal font-semibold" data-testid="price-included">Included</span>;
  return (
    <><span className="tabular font-semibold">{gbp(s.net)}</span><span className="text-slate-400 text-xs ml-2">+ {gbp(s.vat)} VAT</span></>
  );
}

// Consolidated one-off charges (one-off services + fee alignment + custom items) — mirrors the internal builder panel.
function oneOffRows(data, grouped) {
  const t = data.totals || {};
  const rows = [];
  (grouped?.one_off || []).forEach((s) => {
    if (s.presentation !== "included" && Number(s.net) > 0)
      rows.push({ key: `svc-${s.service_id}`, label: s.client_name, amount: s.net, vat: Number(s.vat) || 0 });
  });
  (t.fee_alignment || []).forEach((fa) => {
    if (!fa.excluded && Number(fa.alignment_fee) > 0)
      rows.push({ key: `fa-${fa.service_id}`, label: `Fee alignment — ${fa.service_name}`, amount: fa.alignment_fee, vat: Number(fa.vat) || 0 });
  });
  (t.one_off_items || []).forEach((it, oi) => {
    if (Number(it.price) > 0)
      rows.push({ key: `oo-${it.id || oi}`, label: it.title || "One-off item", amount: it.price, vat: Number(it.vat) || 0, description: it.description });
  });
  return rows;
}

function OneOffCharges({ data, grouped }) {
  const rows = oneOffRows(data, grouped);
  if (rows.length === 0) return null;
  const net = rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const vat = rows.reduce((a, r) => a + (Number(r.vat) || 0), 0);
  return (
    <div className="mt-8" data-testid="one-off-charges-client">
      <div className="text-cass-teal text-sm font-semibold mb-2">One-off charges</div>
      <div className="bg-white/5 rounded-xl overflow-hidden">
        {rows.map((r) => (
          <div key={r.key} className="px-5 py-3 border-b border-white/10 last:border-0" data-testid={`one-off-charge-${r.key}`}>
            <div className="flex justify-between items-start gap-4">
              <span className="text-slate-200">{r.label}</span>
              <span className="tabular font-semibold text-slate-100 shrink-0">{gbp(r.amount)} <span className="text-slate-400 text-xs">+ {gbp(r.vat)} VAT</span></span>
            </div>
            {r.description && <p className="text-sm text-slate-400 mt-1 whitespace-pre-line">{r.description}</p>}
          </div>
        ))}
      </div>
      <TotalBlock testid="one-off-total-block" totalTestid="one-off-charges-total" net={net} vat={vat} totalLabel="Total one-off" />
      {data.alignment_plan?.schedule?.length > 0 && (
        <div className="mt-5" data-testid="alignment-schedule-client">
          <div className="text-cass-teal text-sm font-semibold mb-2">Fee Alignment — how it's paid</div>
          <p className="text-slate-300 text-sm mb-2">Spread over {data.alignment_plan.instalments} monthly payment{data.alignment_plan.instalments === 1 ? "" : "s"} ({data.alignment_plan.start_month} – {data.alignment_plan.end_month}), separate from your ongoing monthly fee. Amounts shown exclude VAT.</p>
          <div className="bg-white/5 rounded-xl overflow-hidden">
            {data.alignment_plan.schedule.map((s, k) => (
              <div key={k} className="flex justify-between px-5 py-2.5 border-b border-white/10 last:border-0"><span className="text-slate-200">{s.n}. {s.month}</span><span className="tabular font-medium text-slate-100">{gbp(s.amount)} + VAT</span></div>
            ))}
            <div className="flex justify-between px-5 py-3 bg-white/10"><span className="text-slate-200 text-sm">Total Fee Alignment</span><span className="tabular font-bold text-slate-50">{gbp(data.alignment_plan.gross)} + VAT</span></div>
          </div>
        </div>
      )}
    </div>
  );
}

function VariationSummary({ data }) {
  const [open, setOpen] = useState(false);
  const vd = data.variation_delta;
  if (!vd) return null;
  const showOneOff = (vd.added_oneoff || []).length > 0 || (vd.removed_oneoff || []).length > 0 || vd.old_oneoff !== vd.new_oneoff;
  const changeCount = (vd.answer_changes || []).length + (vd.added_recurring || []).length + (vd.removed_recurring || []).length + (vd.added_oneoff || []).length + (vd.removed_oneoff || []).length;
  return (
    <section className="px-6 md:px-12 py-10 bg-cass-bone" data-testid="client-variation-summary">
      <div className="max-w-3xl mx-auto">
        <div className="text-xs font-semibold uppercase tracking-widest mb-3 text-cass-teal">What's changing</div>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-cass-navy mb-1">Your updated agreement</h2>
        <p className="text-cass-slate text-sm mb-4">This variation updates your existing agreement{data.variation_source_ref ? ` (${data.variation_source_ref})` : ""}. Everything else stays the same.</p>

        <div className="bg-white rounded-xl ring-1 ring-border overflow-hidden">
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm text-cass-slate">{changeCount} {changeCount === 1 ? "change" : "changes"} to your agreement</div>
              <div className="mt-1 font-semibold text-cass-navy">Monthly recurring: <span className="tabular">{gbp(vd.old_monthly)} → {gbp(vd.new_monthly)}</span></div>
              {showOneOff && <div className="text-sm text-cass-navy">One-off: <span className="tabular">{gbp(vd.old_oneoff)} → {gbp(vd.new_oneoff)}</span></div>}
            </div>
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid="variation-compare-toggle"
              className="inline-flex items-center gap-2 rounded-full border border-cass-navy/20 px-4 py-2 text-sm font-medium text-cass-navy hover:bg-cass-navy hover:text-white transition-colors">
              {open ? "Hide comparison" : "Compare to current agreement"}
              <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </div>

          {open && (
            <div className="border-t border-border px-5 py-5" data-testid="variation-compare-detail">
              <h3 className="font-semibold text-cass-navy mb-2">Monthly recurring fees</h3>
              {changeCount === 0 && <p className="text-sm text-cass-slate">No line changes — totals updated only.</p>}
              <div className="space-y-2">
                {(vd.answer_changes || []).map((x, i) => (<div key={`u${i}`} className="bg-cass-bone rounded-lg px-4 py-2 ring-1 ring-amber-200"><span className="text-amber-700 font-medium">{x.label}: {x.from} → {x.to}</span></div>))}
                {(vd.added_recurring || []).map((x, i) => (<div key={`ar${i}`} className="bg-cass-bone rounded-lg px-4 py-2 ring-1 ring-emerald-200"><span className="text-emerald-700 font-medium">Added · {x.name}</span></div>))}
                {(vd.removed_recurring || []).map((x, i) => (<div key={`rr${i}`} className="bg-cass-bone rounded-lg px-4 py-2 ring-1 ring-red-200"><span className="text-red-700 font-medium">Removed · {x.name}</span></div>))}
              </div>
              <div className="flex justify-between mt-3 pt-3 border-t border-border font-semibold text-cass-navy"><span>New monthly recurring total</span><span className="tabular">{gbp(vd.old_monthly)} → {gbp(vd.new_monthly)}</span></div>
              {showOneOff && (
                <div>
                  <h3 className="font-semibold text-cass-navy mt-6 mb-2">One-off fees</h3>
                  <div className="space-y-2">
                    {(vd.added_oneoff || []).map((x, i) => (<div key={`ao${i}`} className="bg-cass-bone rounded-lg px-4 py-2 ring-1 ring-emerald-200"><span className="text-emerald-700 font-medium">Added · {x.name}</span></div>))}
                    {(vd.removed_oneoff || []).map((x, i) => (<div key={`ro${i}`} className="bg-cass-bone rounded-lg px-4 py-2 ring-1 ring-red-200"><span className="text-red-700 font-medium">Removed · {x.name}</span></div>))}
                  </div>
                  <div className="flex justify-between mt-3 pt-3 border-t border-border font-semibold text-cass-navy"><span>One-off total</span><span className="tabular">{gbp(vd.old_oneoff)} → {gbp(vd.new_oneoff)}</span></div>
                </div>
              )}
            </div>
          )}
        </div>
        <p className="text-xs text-cass-slate mt-2">These changes take effect from your next billing cycle.</p>
      </div>
    </section>
  );
}

function Section({ id, kicker, title, children, dark }) {
  return (
    <section id={id} className={`py-14 px-6 md:px-12 ${dark ? "bg-cass-navy text-white" : ""}`}>
      <div className="max-w-3xl mx-auto">
        {kicker && <div className="text-xs font-semibold uppercase tracking-widest mb-3 text-cass-teal">{kicker}</div>}
        {title && <h2 className={`font-display text-3xl md:text-4xl font-bold tracking-tight mb-6 ${dark ? "text-white" : "text-cass-navy"}`}>{title}</h2>}
        {children}
      </div>
    </section>
  );
}

function LogosWall({ testimonials }) {
  const logos = (testimonials || []).filter((t) => t.logo_path);
  if (!logos.length) return null;
  return (
    <div className="bg-white border-b border-border py-7 px-6 md:px-12" data-testid="web-logos-wall">
      <p className="text-center text-xs font-semibold uppercase tracking-widest text-cass-slate/50 mb-5">Trusted by businesses like yours</p>
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 max-w-4xl mx-auto">
        {logos.map((t) => (
          <img key={t.id} src={`${BK}/api/public/files/${t.logo_path}`} alt={t.role || t.author || "Client logo"}
            className="h-8 md:h-9 w-auto object-contain grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
            data-testid={`logo-wall-item-${t.id}`} />
        ))}
      </div>
    </div>
  );
}

function WebPages({ pages, data, grouped, isRenewal, pkg, selectedOptionals = [], onToggleOptional, canSelect }) {
  const expanded = [];
  pages.forEach((pg) => { expanded.push(pg); if (pg.extra_blocks?.length) expanded.push({ type: "content", anchor: `${pg.anchor}-x`, blocks: pg.extra_blocks }); });
  const isVariation = data.proposal_type === "variation";
  let varShown = false;
  return (
    <>
      {expanded.map((pg, i) => {
        if (pg.type === "cover") return (
          <Fragment key={i}>
          <section id={pg.anchor} className="relative pt-20 pb-16 px-6 md:px-12 text-white grain overflow-hidden" style={{ background: pg.bg_color }} data-testid="web-cover">
            {pg.bg_image && <><img src={`${BK}${pg.bg_image}`} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="web-cover-bg" /><div className="absolute inset-0 bg-cass-navy/70" /></>}
            <div className="max-w-3xl mx-auto relative z-10">
              <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-4">{isVariation ? "Variation" : isRenewal ? "Renewal" : "Proposal"} · Prepared for</div>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{pg.prepared_for}</h1>
              {pg.subtitle && <p className="text-slate-300 text-lg mt-4 max-w-xl">{pg.subtitle}</p>}
              {pg.tagline && <p className="text-slate-400 mt-1">{pg.tagline}</p>}
              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-400">
                {pg.reference && <div><div className="text-slate-500">Reference</div>{pg.reference}</div>}
                {pg.date && <div><div className="text-slate-500">Issued</div>{pg.date}</div>}
                {pg.expiry && <div><div className="text-slate-500">Valid until</div>{pg.expiry}</div>}
              </div>
              {pg.blocks?.length > 0 && (
                <div className="mt-8 space-y-3" data-testid="web-cover-blocks">
                  {pg.blocks.map((b, j) => {
                    if (b.type === "heading") { const T = b.level === 3 ? "h3" : "h2"; return <T key={j} className="font-display font-bold text-white">{b.text}</T>; }
                    if (b.type === "text") return <div key={j} className="rt-block text-slate-200 [&_*]:text-slate-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: b.html }} />;
                    if (b.type === "image") return <figure key={j} className="my-4"><img src={`${BK}${b.url}`} alt={b.caption || ""} style={{ maxWidth: `${b.width_pct || 100}%` }} className="rounded-xl mx-auto block max-h-[70vh] object-contain" />{b.caption && <figcaption className="text-sm text-slate-400 mt-2 text-center">{b.caption}</figcaption>}</figure>;
                    if (b.type === "quote") return <blockquote key={j} className="border-l-2 border-cass-teal pl-4 italic text-slate-200">{b.text}</blockquote>;
                    if (b.type === "divider") return <hr key={j} className="my-6 border-white/20" />;
                    return null;
                  })}
                </div>
              )}
            </div>
          </section>
          <LogosWall testimonials={data.testimonials} />
          </Fragment>
        );
        if (pg.type === "toc") return (
          <Section key={i} id={pg.anchor} kicker="Contents" title={pg.title}>
            <ol className="space-y-2">{pg.items.map((it, j) => <li key={j}><a href={`#${it.anchor}`} className="text-cass-navy hover:text-cass-teal font-medium">{j + 1}. {it.title}</a></li>)}</ol>
          </Section>
        );
        if (pg.type === "content") return (
          <Section key={i} id={pg.anchor}>
            <div className="doc-prose text-cass-slate text-lg">
              {pg.blocks.map((b, j) => {
                if (b.type === "heading") { const T = b.level === 3 ? "h3" : "h2"; return <T key={j}>{b.text}</T>; }
                if (b.type === "text") return <div key={j} className="rt-block" dangerouslySetInnerHTML={{ __html: b.html }} />;
                if (b.type === "image") return <figure key={j} className="my-6"><img src={`${BK}${b.url}`} alt={b.caption || ""} style={{ maxWidth: `${b.width_pct || 100}%` }} className="rounded-xl border border-border mx-auto block max-h-[70vh] object-contain" />{b.caption && <figcaption className="text-sm text-cass-slate mt-2 text-center">{b.caption}</figcaption>}</figure>;
                if (b.type === "quote") return <blockquote key={j} className="border-l-2 border-cass-teal pl-4 italic">{b.text}</blockquote>;
                if (b.type === "divider") return <hr key={j} className="my-8 border-border" />;
                return null;
              })}
            </div>
          </Section>
        );
        if (pg.type === "testimonials") return (
          <Section key={i} id={pg.anchor} kicker="Client stories" title={pg.title}>
            <div className="grid md:grid-cols-2 gap-6">{pg.items.map((t, j) => <div key={j} className="relative overflow-hidden bg-white border border-border rounded-xl p-6"><span aria-hidden className="pointer-events-none absolute -top-4 right-4 font-serif text-8xl leading-none text-cass-navy/5 select-none">“</span>{t.quote && <p className="relative text-cass-slate italic leading-relaxed">"{t.quote}"</p>}{t.image_path && <img src={`${BK}/api/public/files/${t.image_path}`} alt="" className="mt-3 max-h-48 rounded-lg border border-border" />}<div className="relative mt-4 flex items-center gap-3">{t.logo_path && <img src={`${BK}/api/public/files/${t.logo_path}`} alt="" className="h-10 w-auto object-contain shrink-0" />}<div><div className="font-semibold text-cass-navy">{t.author}</div><div className="text-sm text-cass-slate">{t.role}</div></div></div></div>)}</div>
          </Section>
        );
        if (pg.type === "pricing_summary") {
          const showVar = isVariation && data.variation_delta && !varShown;
          if (showVar) varShown = true;
          return (
          <Fragment key={i}>
          {showVar && <VariationSummary data={data} />}
          <section id={pg.anchor} className="py-14 px-6 md:px-12 bg-cass-navy text-white" data-testid="web-pricing">
            <div className="max-w-3xl mx-auto">
              <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-3">Your investment</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">{pg.title}</h2>
              {pg.intro && <p className="text-slate-300 mb-6">{pg.intro}</p>}
              {isRenewal && data.fee_comparison && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8 bg-white/5 rounded-xl p-6">
                  <div><div className="text-slate-400 text-sm">Current</div><div className="text-2xl font-bold tabular">{gbp(data.fee_comparison.current)}/mo</div></div>
                  <div><div className="text-slate-400 text-sm">Proposed</div><div className="text-2xl font-bold tabular">{gbp(data.fee_comparison.proposed)}/mo</div></div>
                  <div><div className="text-slate-400 text-sm">Change</div><div className="text-2xl font-bold tabular text-cass-teal">{gbp(data.fee_comparison.change)} ({data.fee_comparison.change_pct}%)</div></div>
                </div>
              )}
              <div className="space-y-6">
                {["monthly", "quarterly", "annual", "optional"].filter((f) => (grouped[f] || []).length && (f === "optional" || isMeaningfulRecurring(grouped[f]))).map((f) => [f, grouped[f]]).map(([freq, items]) => (
                  <div key={freq}>
                    <div className="text-cass-teal text-sm font-semibold mb-2">{FREQ_LABELS[freq] || freq}</div>
                    {freq === "optional" && onToggleOptional && (
                      <div className="mb-2 text-xs text-cass-teal bg-cass-teal/10 rounded-md px-3 py-1.5 inline-flex" data-testid="extras-summary">
                        {(() => {
                          const chosen = items.filter((x) => selectedOptionals.includes(x.service_id));
                          const add = chosen.filter((x) => x.billing_frequency === "monthly").reduce((a, x) => a + (x.net || 0), 0);
                          return `${chosen.length} of ${items.length} extra${items.length === 1 ? "" : "s"} added${add ? ` · +${gbp(add)}/mo` : ""}`;
                        })()}
                      </div>
                    )}
                    <div className="bg-white/5 rounded-xl overflow-hidden">
                      {items.map((s, k) => (
                        <div key={k} className="flex items-start justify-between px-5 py-3 border-b border-white/10 last:border-0">
                          <label className={`text-slate-200 flex items-start gap-2 ${freq === "optional" && onToggleOptional && canSelect ? "cursor-pointer" : ""}`}>
                            {freq === "optional" && onToggleOptional && (
                              <input type="checkbox" checked={selectedOptionals.includes(s.service_id)} disabled={!canSelect}
                                onChange={() => onToggleOptional(s.service_id)} className="accent-cass-teal w-4 h-4 mt-1" data-testid={`opt-select-${s.service_id}`} />
                            )}
                            <span>
                              <span className="block">{s.client_name}</span>
                              {(s.client_params || []).length > 0 && (
                                <span className="block mt-1" data-testid={`svc-params-${s.service_id}`}>
                                  {s.client_params.map((p, pi) => (
                                    <span key={pi} className="block text-xs text-slate-400">• {p.label}: {p.value}</span>
                                  ))}
                                </span>
                              )}
                            </span>
                          </label>
                          <div className="text-right pl-4 shrink-0"><PriceCell s={s} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {(() => {
                const recTotals = recurringTotals(grouped, selectedOptionals);
                return recTotals.map((rt, idx) => (
                  <TotalBlock key={rt.freq} testid={`recurring-total-${rt.freq}`} totalTestid={rt.freq === "monthly" ? "monthly-total-value" : undefined} net={rt.net} vat={rt.vat} totalLabel={REC_TOTAL_LABEL[rt.freq]} discount={idx === 0 ? data.discount : undefined} />
                ));
              })()}
              <OneOffCharges data={data} grouped={grouped} />
              {data.discount && recurringTotals(grouped, selectedOptionals).length === 0 && <div className="mt-6 flex justify-end text-slate-300 text-sm">Subtotal {gbp(data.subtotal?.net)} · <span className="text-cass-teal ml-1">{data.discount.label}{data.discount.reason ? ` (${data.discount.reason})` : ""} −{gbp(data.discount.amount)}</span></div>}
            </div>
          </section>
          </Fragment>
        );
        }
        if (pg.type === "pricing_factors") return (
          <Section key={i} id={pg.anchor} kicker="How your price is built" title={pg.title}>
            <div className="space-y-6">{pg.sections.map((sec, j) => (<div key={j}><h3 className="font-alt font-semibold text-cass-navy mb-2">{sec.section}</h3><div className="bg-white border border-border rounded-xl overflow-hidden">{sec.rows.map((r, k) => <div key={k} className="flex justify-between px-5 py-2.5 border-b border-border last:border-0"><span className="text-cass-slate">{r.label}</span><span className="text-cass-navy font-medium">{r.value}</span></div>)}</div></div>))}</div>
          </Section>
        );
        if (pg.type === "catchup") return (
          <Section key={i} id={pg.anchor} kicker="Bringing you up to date" title={pg.title}>
            <p className="text-cass-slate text-lg whitespace-pre-line">{pg.explanation}</p>
            {data.catch_up_items?.length > 0 && (
              <div className="mt-6 bg-white border border-border rounded-xl overflow-hidden">
                {data.catch_up_items.map((c, k) => <div key={k} className="flex justify-between px-5 py-3 border-b border-border last:border-0"><span className="text-cass-slate">{c.name} <span className="text-xs text-cass-slate/60">· {c.period}</span></span><span className="tabular font-semibold text-cass-navy">{gbp(c.net)}</span></div>)}
                <div className="flex justify-between px-5 py-3 bg-cass-bone"><span className="text-cass-slate text-sm">Catch-Up Fee (total){(data.catch_up_due_date && (!data.catch_up_plan?.schedule?.length)) ? ` · due ${data.catch_up_due_date}` : ""}</span><span className="tabular font-bold text-cass-navy">{gbp(data.catch_up_totals?.gross)}</span></div>
              </div>
            )}
            {data.catch_up_plan?.schedule?.length > 0 && (
              <div className="mt-6" data-testid="catchup-schedule">
                <p className="text-cass-navy font-medium mb-2">How it's paid: spread over {data.catch_up_plan.instalments} monthly payment{data.catch_up_plan.instalments === 1 ? "" : "s"} ({data.catch_up_plan.start_month} – {data.catch_up_plan.end_month}), separate from your ongoing monthly fee.</p>
                <div className="bg-white border border-border rounded-xl overflow-hidden">
                  {data.catch_up_plan.schedule.map((s, k) => <div key={k} className="flex justify-between px-5 py-2.5 border-b border-border last:border-0"><span className="text-cass-slate">{s.n}. {s.month}</span><span className="tabular font-medium text-cass-navy">{gbp(s.amount)}</span></div>)}
                  <div className="flex justify-between px-5 py-3 bg-cass-bone"><span className="text-cass-slate text-sm">Total Catch-Up Fee</span><span className="tabular font-bold text-cass-navy">{gbp(data.catch_up_plan.gross)}</span></div>
                </div>
              </div>
            )}
          </Section>
        );
        if (pg.type === "service_details") return (
          <Section key={i} id={pg.anchor} kicker="Recommended services" title={pg.title}>
            <div className="grid md:grid-cols-2 gap-4">{pg.services.map((s, j) => <div key={j} className="bg-white border border-border rounded-xl p-5"><h3 className="font-alt font-semibold text-cass-navy mb-2">{s.name}</h3><ul className="space-y-1">{s.items.map((it, k) => <li key={k} className="text-sm text-cass-slate flex gap-2"><span className="text-cass-teal">•</span>{it}</li>)}</ul></div>)}</div>
          </Section>
        );
        if (pg.type === "next_steps") return (
          <Section key={i} id={pg.anchor} kicker="What happens next" title={pg.title}>
            <div className="grid md:grid-cols-3 gap-6">{pg.steps.map((t, j) => <div key={j} className="bg-white border border-border rounded-xl p-6"><div className="w-9 h-9 rounded-full bg-cass-navy text-white grid place-items-center font-bold mb-3">{j + 1}</div><p className="text-cass-slate">{t}</p></div>)}</div>
          </Section>
        );
        if (pg.type === "packages") return (
          <Section key={i} id={pg.anchor} kicker="Your selected plan" title={pg.title}>
            {pg.intro && <p className="text-cass-slate text-lg mb-6">{pg.intro}</p>}
            <div className="grid md:grid-cols-3 gap-4">
              {pg.tiers.map((tier, j) => {
                const active = pkg === tier.name;
                return (
                  <div key={j} className={`text-left bg-white border rounded-xl p-6 ${active ? "border-cass-teal ring-2 ring-cass-teal" : "border-border opacity-70"}`} data-testid={`pkg-${j}`}>
                    {tier.highlighted && <span className="text-xs font-semibold text-cass-teal">MOST POPULAR</span>}
                    <h3 className="font-display text-2xl text-cass-navy">{tier.name}</h3>
                    <div className="text-cass-navy font-bold text-xl mt-1">{tier.price} <span className="text-sm text-cass-slate font-normal">{tier.frequency}</span></div>
                    <ul className="mt-4 space-y-1">{tier.features.map((f, k) => <li key={k} className="text-sm text-cass-slate flex gap-2"><span className="text-cass-teal">✓</span>{f}</li>)}</ul>
                    {active && <div className="mt-4 text-center text-sm font-semibold rounded-full py-2 bg-cass-teal text-white">Selected</div>}
                  </div>
                );
              })}
            </div>
          </Section>
        );
        if (pg.type === "contact") return (
          <Section key={i} id={pg.anchor} kicker="Get in touch" title={pg.title}>
            {pg.message && <p className="text-cass-slate text-lg mb-4">{pg.message}</p>}
            <p className="text-cass-slate">{pg.phone} · {pg.email}</p>
            <p className="text-cass-slate text-sm mt-1">{pg.hours}</p>
            <p className="text-cass-slate text-sm">{pg.address}</p>
          </Section>
        );
        return null;
      })}
    </>
  );
}

// Read-only proposal document body (template-driven web pages, with legacy fallback).
export default function DocumentBody({ data, grouped, isRenewal, pkg, selectedOptionals = [], onToggleOptional, canSelect }) {
  if (data.web_pages?.length > 0) {
    return <WebPages pages={data.web_pages} data={data} grouped={grouped} isRenewal={isRenewal} pkg={pkg} selectedOptionals={selectedOptionals} onToggleOptional={onToggleOptional} canSelect={canSelect} />;
  }
  return (
    <>
      <section className="relative pt-20 pb-16 px-6 md:px-12 bg-cass-navy text-white grain overflow-hidden">
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-4">{data.proposal_type === "variation" ? "Variation" : isRenewal ? "Renewal" : "Proposal"} · Prepared for</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{data.client_name}</h1>
          <p className="text-slate-300 text-lg mt-4 max-w-xl">Your Outsourced Finance Team for Growing Businesses. Giving You More Than Numbers.</p>
          <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-400">
            <div><div className="text-slate-500">Reference</div>{data.reference}</div>
            {data.issue_date && <div><div className="text-slate-500">Issued</div>{data.issue_date}</div>}
            {data.expiry_date && <div><div className="text-slate-500">Valid until</div>{data.expiry_date}</div>}
            {isRenewal && data.effective_date && <div><div className="text-slate-500">Effective from</div>{data.effective_date}</div>}
          </div>
        </div>
      </section>

      <LogosWall testimonials={data.testimonials} />

      {data.document_blocks?.length > 0 && (
        <Section id="about" kicker="Your proposal" title="Working alongside you, not at a distance">
          <div className="doc-prose text-cass-slate text-lg">
            {data.document_blocks.map((b, i) => {
              if (b.type === "heading") { const Tag = b.level === 3 ? "h3" : "h2"; return <Tag key={i}>{b.text}</Tag>; }
              if (b.type === "text") return <div key={i} dangerouslySetInnerHTML={{ __html: b.html }} />;
              if (b.type === "image") return (
                <figure key={i} className="my-6"><img src={`${BK}${b.url}`} alt={b.caption || ""} style={{ maxWidth: `${b.width_pct || 100}%` }} className="rounded-xl border border-border mx-auto block max-h-[70vh] object-contain" />{b.caption && <figcaption className="text-sm text-cass-slate mt-2 text-center">{b.caption}</figcaption>}</figure>
              );
              if (b.type === "divider") return <hr key={i} className="my-8 border-border" />;
              return null;
            })}
          </div>
        </Section>
      )}

      {data.testimonials?.length > 0 && (
        <Section id="testimonials" kicker="Client stories" title="What our clients say">
          <div className="grid md:grid-cols-2 gap-6">
            {data.testimonials.map((t) => (
              <div key={t.id} className="relative overflow-hidden bg-white border border-border rounded-xl p-6">
                <span aria-hidden className="pointer-events-none absolute -top-4 right-4 font-serif text-8xl leading-none text-cass-navy/5 select-none">“</span>
                {t.quote && <p className="relative text-cass-slate italic leading-relaxed">"{t.quote}"</p>}
                {t.image_path && <img src={`${BK}/api/public/files/${t.image_path}`} alt="" className="mt-3 max-h-48 rounded-lg border border-border" />}
                <div className="relative mt-4 flex items-center gap-3">
                  {t.logo_path && <img src={`${BK}/api/public/files/${t.logo_path}`} alt="" className="h-10 w-auto object-contain shrink-0" />}
                  <div>
                    <div className="font-semibold text-cass-navy">{t.author}</div>
                    <div className="text-sm text-cass-slate">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {data.assumptions?.length > 0 && (
        <Section id="understand" kicker="What we understand" title="About your business">
          <ul className="space-y-3">
            {data.assumptions.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-cass-slate text-lg"><Check className="w-5 h-5 text-cass-teal mt-1 shrink-0" />{a}</li>
            ))}
          </ul>
        </Section>
      )}

      <Section id="services" kicker="Recommended services" title="What we'll provide">
        <div className="grid md:grid-cols-2 gap-4">
          {data.services.map((s, i) => (
            <div key={i} className="bg-white border border-border rounded-xl p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-alt font-semibold text-cass-navy">{s.client_name}</h3>
                <span className="text-xs px-2 py-0.5 rounded-full bg-cass-bone text-cass-slate">{s.presentation}</span>
              </div>
              <div className="text-sm text-cass-slate mt-1">{s.category}</div>
              {(s.client_params || []).length > 0 && (
                <ul className="mt-2 space-y-0.5" data-testid={`svc-grid-params-${s.service_id}`}>
                  {s.client_params.map((p, pi) => (
                    <li key={pi} className="text-xs text-cass-slate">• {p.label}: <span className="font-medium text-cass-navy">{p.value}</span></li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </Section>

      <VariationSummary data={data} />

      <Section id="investment" kicker="Your investment" title="Pricing tailored to you" dark>
        <div className="space-y-6">
          {["monthly", "quarterly", "annual", "optional"].filter((f) => (grouped[f] || []).length).map((f) => [f, grouped[f]]).map(([freq, items]) => (
            <div key={freq}>
              <div className="text-cass-teal text-sm font-semibold mb-2">{FREQ_LABELS[freq] || freq}</div>
              <div className="bg-white/5 rounded-xl overflow-hidden">
                {items.map((s, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-3 border-b border-white/10 last:border-0">
                    <span className="text-slate-200">{s.client_name}</span>
                    <div className="text-right"><PriceCell s={s} /></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        {recurringTotals(grouped, selectedOptionals).map((rt) => (
          <TotalBlock key={rt.freq} testid={`recurring-total-${rt.freq}`} net={rt.net} vat={rt.vat} totalLabel={REC_TOTAL_LABEL[rt.freq]} />
        ))}
        <OneOffCharges data={data} grouped={grouped} />
      </Section>
    </>
  );
}
